import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { mailcheckImg } from "../../assets/image";
import { FirstButton } from "../../components/buttons/buttons";
import { TextField } from "../../components/inputs/textfield";
import { useState } from "react";
import { initUserData, useGlobalContext } from "../../provider";
import { apiNotification } from "../../services/apiNotification";
import { restApi } from "../../provider/restApi";
import { useCustomNavigate } from "../../provider/navigate";
import { toastMessage } from "../../utils";

interface MailVerifyCheckerProps {
	data: SignUpProps
	verifyCode: string
}

interface StateObject {
	verifyCode: number | null
	errorVerifyCode: boolean
}

const initState: StateObject = {
	verifyCode: null,
	errorVerifyCode: false
}

const MailVerifyChecker = ({ data, verifyCode }: MailVerifyCheckerProps) => {
	const [state, setState] = useState<StateObject>(initState);
	const [, { dispatch }]: GlobalContextType = useGlobalContext();
	const { navigate } = useCustomNavigate();

	const onChangeState = (updateData: Partial<StateObject>) => {
		setState({ ...state, ...updateData });
	}

	const onChangeInputValue = (key: string) => (e: any) => {
		const { name, value } = e.target;
		const code = value.toString().slice(0, 6)
		onChangeState({ [name]: Number(code) || null, [key]: false });
	}

	const checkValidate = (code: number): boolean => {
		const codeLength = code.toString().length;
		if (codeLength !== 6) return false;

		return true;
	}

	const onVerifyEmail = async () => {
		try {
			const verifyCode = Number(state.verifyCode);
			const validate = checkValidate(verifyCode);

			if (!validate) {
				onChangeState({ errorVerifyCode: true });
				return false;
			}

			dispatch({ loading: true });

			const reqData: VerifyCodeProps = {
				email: data.email, verifyCode: verifyCode.toString()
			}

			const resData = await restApi.mailVerify(reqData);
			dispatch({
				loading: false,
				loginStatus: true,
				authToken: resData.token,
				userData: resData.userData,
			})

			navigate("/");
			toastMessage("success", "Register successed!");
		} catch (error) {
			dispatch({
				loading: false,
				loginStatus: false,
				authToken: "",
				userData: initUserData,
			})

			apiNotification(error, "Verify Email failed!");
		}
	}

	return (
		<MailVerifyCheckerWrapper>
			<MailVerifyCheckerContainer>
				<img src={mailcheckImg} alt="mail-check" />
				<Typography variant="h2">Email Verification Required</Typography>

				<Typography variant="body1">
					Please check your email inbox for a verification link to confirm your account.<br />
					Click the link to verify your email address and gain access to all features.<br />
					If you don't see the email, be sure to check your spam or junk folder.
				</Typography>

				<TextField fullWidth
					name="verifyCode"
					type="number" sx={{ my: 2 }}
					placeholder="Enter you verification code"
					helperText="Enter your 6-digit verification code"
					onChange={onChangeInputValue("errorVerifyCode")}
					error={state.errorVerifyCode}
					value={state.verifyCode}
				/>

				<FirstButton fullWidth onClick={onVerifyEmail}>
					<Typography variant="body1">
						Verify Email
					</Typography>
				</FirstButton>
			</MailVerifyCheckerContainer>
		</MailVerifyCheckerWrapper>
	)
}

const MailVerifyCheckerWrapper = styled(Box)(({ theme }) => ({
	left: 0, top: 0,
	position: "fixed",

	zIndex: 2000,
	width: "100vw",
	height: "100vh",

	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: theme.palette.common.mainBg,
}))

const MailVerifyCheckerContainer = styled(Box)(({ theme }) => ({
	width: 650,
	maxWidth: "100%",
	maxHeight: "100%",

	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	textAlign: "center",

	gap: theme.spacing(2),
	padding: theme.spacing(5),
	borderRadius: theme.spacing(3),
	boxShadow: `0px 0px 10px ${theme.palette.common.firstBorder}`,

	img: {
		width: 300,
		height: "auto",
	},

	[theme.breakpoints.down("md")]: {
		borderRadius: theme.spacing(1),
	}
}))

export { MailVerifyChecker };